// Grids & Gantts — the ways a folder groups its rows.
//
// A grouping brackets a folder's rows into headers. It used to be one thing:
// the value of a field. It is five now, and every one answers the same
// question the same way — "which group is this work item in, what is the
// group called, and where does it sort" — as { key, label, order }:
//
//   field     the field's value ("Client Name"), headers in alphabetical order
//   date      a date field by DAY / WEEK / MONTH / QUARTER / YEAR, headers in
//             date order, "Mar 2026", undated last
//   preset    a ready-made grouping: status category, work type, project,
//             project category, assignee, priority, done / not done, or the
//             due bands (overdue / this week / this month / later / no date)
//   zql       named ZQL buckets, in order: a work item lands in the FIRST
//             bucket whose query it matches, the rest in "(other)"; answered
//             by the server over the structure's keys, like a quick button
//   formula   a small JavaScript expression per work item, with helpers for
//             the fields and the dates; returns a string, a number, a date
//             or { key, label, order }
//
// Pure: no React, no network. The page hands in what a kind needs (a field
// id, the bucket answers, a name resolver); the engine only ever sees the
// { key, label, order } that comes out.

export const GROUP_KINDS = [
  { id: 'field', label: 'The value of a field' },
  { id: 'date', label: 'A date, by day / week / month / quarter / year' },
  { id: 'preset', label: 'A ready-made grouping' },
  { id: 'zql', label: 'ZQL buckets, in order' },
  { id: 'formula', label: 'A formula' },
];

export const DATE_GRAINS = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'quarter', label: 'Quarter' },
  { id: 'year', label: 'Year' },
];

// `fields` names what the page must LOAD for the preset to have anything to
// read; status, type, project and the due date are always loaded.
export const PRESETS = [
  { id: 'statusCategory', label: 'Status category — To do / In progress / Done' },
  { id: 'type', label: 'Work type' },
  { id: 'project', label: 'Project' },
  { id: 'projectCategory', label: 'Project category' },
  { id: 'assignee', label: 'Assignee', fields: ['assignee'] },
  { id: 'priority', label: 'Priority', fields: ['priority'] },
  { id: 'done', label: 'Done / not done' },
  { id: 'dueBand', label: 'Due — overdue / this week / this month / later / no date' },
];

const LAST = Number.MAX_SAFE_INTEGER;
export const NONE = Object.freeze({ key: '(none)', label: '(none)', order: LAST });
export const PENDING = Object.freeze({ key: '~pending', label: 'applying the buckets…', order: -1 });

const str = (v, d = '') => (typeof v === 'string' ? v : d);
const list = (v) => (Array.isArray(v) ? v : []);
const oneOf = (v, opts, d) => (opts.some((o) => o.id === v) ? v : d);

// A grouping as saved. An older config carried { id, field } and nothing
// else — that is a 'field' grouping, unchanged.
export function normalizeGroup(g, i = 0) {
  const r = g && typeof g === 'object' ? g : {};
  const kind = oneOf(r.kind, GROUP_KINDS, 'field');
  return {
    id: str(r.id) || `g${i}`,
    kind,
    field: str(r.field),
    grain: oneOf(r.grain, DATE_GRAINS, 'month'),
    preset: oneOf(r.preset, PRESETS, 'statusCategory'),
    buckets: list(r.buckets).map((b, j) => ({ id: str(b && b.id) || `k${j}`, label: str(b && b.label), zql: str(b && b.zql) })),
    other: str(r.other),
    formula: str(r.formula),
    // A WORK TYPE WHOSE ICON EVERY HEADER WEARS (optional). For a field
    // whose values name work items of one kind but are only text — an
    // "Asset" dropdown whose options are the assets' names — there is no
    // work item to take an icon from, so the admin names the type. Work
    // type and status groupings wear their own look and ignore it.
    iconType: str(r.iconType).trim(),
    // THE HEADERS' ORDER, when the admin gives one (a 'field' grouping):
    // these labels first, in this order, everything else after them A to Z.
    // Empty = A to Z — except Status, which goes To do → In progress → Done
    // first (see statusOrder).
    order: [...new Set(list(r.order).map((x) => str(x).trim()).filter(Boolean))],
  };
}

// A header's place in an admin's hand-made order: its index, or null when
// the list does not name it (then it sorts after the named ones, by label).
export function orderIn(order, label) {
  if (!order || !order.length) return null;
  const want = String(label || '').trim().toLowerCase();
  const i = order.findIndex((x) => String(x).trim().toLowerCase() === want);
  return i < 0 ? null : i;
}

// Where a STATUS header goes when nobody said: by its category, To do →
// In progress → Done, then A to Z inside a category (the engine's label
// compare). The site holds no global workflow order to read instead.
const CAT_RANK = { new: 0, indeterminate: 1, done: 2 };
export const statusRank = (it) => { const c = catOf(it); return c in CAT_RANK ? CAT_RANK[c] : 3; };

// The field names a formula reads by NAME — the string arguments of
// value / number / date and the date-bucket helpers. The page loads these
// with the rows; a field a formula reads through `fields` by id must be on
// a column or a grouping to be there.
const READERS = ['value', 'number', 'date', 'day', 'week', 'month', 'quarter', 'year', 'daysUntil', 'daysSince'];
const BUILT_IN_NAMES = new Set(['duedate', 'created', 'updated', 'summary', 'status', 'issuetype', 'project', 'resolutiondate', 'resolution', 'labels', 'assignee', 'priority']);
export function formulaFieldNames(src) {
  const out = new Set();
  const re = new RegExp(`\\b(?:${READERS.join('|')})\\s*\\(\\s*(["'])((?:\\\\.|(?!\\1).)+)\\1`, 'g');
  let m;
  const text = String(src || '');
  while ((m = re.exec(text))) {
    const n = m[2].trim();
    if (n && !/^\d{4}-\d{2}-\d{2}$/.test(n) && !BUILT_IN_NAMES.has(n)) out.add(n);
  }
  return [...out];
}

// Whether a saved grouping is USABLE — the only thing that makes it a
// grouping at all. A 'field' or 'date' one needs a field, a 'zql' one at
// least one bucket with a query, a 'formula' one a formula.
export function groupIsSet(g) {
  if (!g) return false;
  if (g.kind === 'field' || g.kind === 'date') return !!g.field;
  if (g.kind === 'zql') return (g.buckets || []).some((b) => b.zql && b.zql.trim());
  if (g.kind === 'formula') return !!(g.formula && g.formula.trim());
  return true;
}

// The words on the amber rule row.
export function describeGroup(g) {
  if (!g) return '(none)';
  if (g.kind === 'date') return g.field ? `${g.field}, by ${(DATE_GRAINS.find((d) => d.id === g.grain) || {}).label || g.grain}`.replace(/, by (\w+)/, (m, w) => `, by ${w.toLowerCase()}`) : '(no date field yet)';
  if (g.kind === 'preset') return (PRESETS.find((p) => p.id === g.preset) || {}).label || g.preset;
  if (g.kind === 'zql') { const n = (g.buckets || []).filter((b) => b.zql && b.zql.trim()).length; return n ? `${n} ZQL bucket${n === 1 ? '' : 's'}: ${(g.buckets || []).filter((b) => b.zql && b.zql.trim()).map((b) => b.label || b.zql).join(' · ')}` : '(no buckets yet)'; }
  if (g.kind === 'formula') return g.formula && g.formula.trim() ? `formula: ${g.formula.trim().slice(0, 60)}${g.formula.trim().length > 60 ? '…' : ''}` : '(no formula yet)';
  return g.field || '(no field yet)';
}

// The field ids a config's groupings need loaded beyond what the columns
// ask for: the presets that read a field the page would not otherwise
// carry.
export function groupFieldIds(cfg) {
  const out = new Set();
  ((cfg && cfg.folders) || []).forEach((f) => ((f && f.groups) || []).forEach((g) => {
    if (g && g.kind === 'preset') ((PRESETS.find((p) => p.id === g.preset) || {}).fields || []).forEach((id) => out.add(id));
  }));
  return [...out];
}

// Every ZQL a config's bucket groupings ask the server, trimmed and once.
export function groupZqls(cfg) {
  const out = new Set();
  ((cfg && cfg.folders) || []).forEach((f) => ((f && f.groups) || []).forEach((g) => {
    if (g && g.kind === 'zql') (g.buckets || []).forEach((b) => { const z = str(b && b.zql).trim(); if (z) out.add(z); });
  }));
  return [...out];
}

// ---- dates ------------------------------------------------------------------
const pad = (n) => String(n).padStart(2, '0');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const isoOfValue = (v) => {
  const s = String(v == null ? '' : v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
};
const ymd = (iso) => iso.split('-').map(Number);
const dayNumber = (iso) => { const [y, m, d] = ymd(iso); return Math.round(Date.UTC(y, m - 1, d) / 86400000); };
const fromDayNumber = (n) => { const d = new Date(n * 86400000); return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`; };
// ISO weeks: Monday first, week 1 holds the year's first Thursday.
function isoWeek(iso) {
  const [y, m, d] = ymd(iso);
  const t = new Date(Date.UTC(y, m - 1, d));
  const dow = (t.getUTCDay() + 6) % 7;                 // Monday = 0
  const monday = new Date(t); monday.setUTCDate(t.getUTCDate() - dow);
  const thursday = new Date(monday); thursday.setUTCDate(monday.getUTCDate() + 3);
  const weekYear = thursday.getUTCFullYear();
  const jan4 = new Date(Date.UTC(weekYear, 0, 4));
  const jan4dow = (jan4.getUTCDay() + 6) % 7;
  const week1Monday = new Date(jan4); week1Monday.setUTCDate(jan4.getUTCDate() - jan4dow);
  const week = 1 + Math.round((monday - week1Monday) / (7 * 86400000));
  return { week, year: weekYear, monday: `${monday.getUTCFullYear()}-${pad(monday.getUTCMonth() + 1)}-${pad(monday.getUTCDate())}` };
}
export const fmtDay = (iso) => { const [y, m, d] = ymd(iso); return `${pad(d)}/${pad(m)}/${y}`; };

// THE PERIOD THAT HOLDS TODAY wears a tag beside its header — "This Month"
// on the current month, "This Year" on the current year — so a reader finds
// now in a long run of dated headers without reading every one of them.
// The header itself stays the real period ("Oct 2026"); the tag is extra.
export const NOW_TAGS = { day: 'Today', week: 'This Week', month: 'This Month', quarter: 'This Quarter', year: 'This Year' };

// A date's bucket at a grain: the key sorts chronologically (the bucket's
// first day), the label reads the way a person says it, the order is the
// day number so the engine never has to parse a label. Given the page's
// today (yyyy-mm-dd), the bucket that holds it carries `tag` (NOW_TAGS).
export function dateBucket(value, grain = 'month', todayIso = '') {
  const b = bucketAt(isoOfValue(value), grain);
  if (b === NONE) return NONE;
  const today = isoOfValue(todayIso);
  return today && bucketAt(today, grain).key === b.key ? { ...b, tag: NOW_TAGS[grain] || NOW_TAGS.month } : b;
}
function bucketAt(iso, grain) {
  if (!iso) return NONE;
  const [y, m] = ymd(iso);
  if (grain === 'day') return { key: iso, label: fmtDay(iso), order: dayNumber(iso) };
  if (grain === 'week') { const w = isoWeek(iso); return { key: w.monday, label: `Week ${w.week}, ${w.year} (from ${fmtDay(w.monday)})`, order: dayNumber(w.monday) }; }
  if (grain === 'quarter') { const q = Math.floor((m - 1) / 3); const first = `${y}-${pad(q * 3 + 1)}-01`; return { key: first, label: `Q${q + 1} ${y}`, order: dayNumber(first) }; }
  if (grain === 'year') { const first = `${y}-01-01`; return { key: first, label: String(y), order: dayNumber(first) }; }
  const first = `${y}-${pad(m)}-01`;
  return { key: first, label: `${MONTHS[m - 1]} ${y}`, order: dayNumber(first) };
}

// ---- the values a grouping reads --------------------------------------------
const one = (x) => (x == null ? '' : (typeof x === 'object' ? (x.displayName || x.value || x.name || x.key || '') : String(x)));
export const textOf = (v) => (Array.isArray(v) ? v.map(one).filter(Boolean).join(', ') : one(v));
const f = (it) => (it && it.fields) || {};
export const catOf = (it) => {
  const c = f(it).status && f(it).status.statusCategory;
  const k = String((c && (c.key || c.name)) || '').toLowerCase();
  if (k === 'done') return 'done';
  if (k === 'new' || k === 'to do' || k === 'todo') return 'new';
  if (k === 'indeterminate' || k === 'in progress') return 'indeterminate';
  return k || '';
};

// A project's small avatar: the compact wire's `avatar`, or the smallest of
// a full search answer's `avatarUrls`. '' when there is none.
export const projectAvatar = (p) => {
  if (!p) return '';
  if (typeof p.avatar === 'string' && p.avatar) return p.avatar;
  const a = p.avatarUrls || {};
  return a['16x16'] || a['24x24'] || a['32x32'] || a['48x48'] || '';
};

// A ready-made grouping. `todayIso` is the page's today (yyyy-mm-dd).
export function presetGroup(it, preset, todayIso) {
  const fl = f(it);
  const named = (label, order) => (label ? { key: label, label, order: order == null ? null : order } : NONE);
  switch (preset) {
    case 'statusCategory': {
      const c = catOf(it);
      // Each wears the lozenge of its category's colour (look: 'status').
      if (c === 'new') return { key: 'todo', label: 'To do', order: 0, look: 'status', sample: it };
      if (c === 'indeterminate') return { key: 'inprogress', label: 'In progress', order: 1, look: 'status', sample: it };
      if (c === 'done') return { key: 'done', label: 'Done', order: 2, look: 'status', sample: it };
      return NONE;
    }
    case 'type': { const g = named(one(fl.issuetype)); return g !== NONE ? { ...g, look: 'type', sample: it } : g; }
    case 'project': {
      // The header wears the project's avatar, as a structure draws it.
      const g = named(fl.project ? (fl.project.name || fl.project.key || '') : '');
      const icon = projectAvatar(fl.project);
      return icon && g !== NONE ? { ...g, icon } : g;
    }
    case 'projectCategory': return named(fl.project && fl.project.projectCategory ? one(fl.project.projectCategory) : '');
    case 'assignee': return fl.assignee ? named(one(fl.assignee)) : { key: '~unassigned', label: 'Unassigned', order: LAST };
    case 'priority': return named(one(fl.priority));
    case 'done': return catOf(it) === 'done' ? { key: 'done', label: 'Done', order: 1 } : { key: 'open', label: 'Not done', order: 0 };
    case 'dueBand': {
      const due = isoOfValue(fl.duedate);
      if (!due) return { key: '~nodate', label: 'No due date', order: 4 };
      const today = isoOfValue(todayIso) || fromDayNumber(Math.floor(Date.now() / 86400000));
      const dn = dayNumber(due); const tn = dayNumber(today);
      if (dn < tn) return { key: 'overdue', label: 'Overdue', order: 0 };
      const wk = isoWeek(today); const weekEnd = dayNumber(wk.monday) + 6;
      if (dn <= weekEnd) return { key: 'thisweek', label: 'This week', order: 1 };
      if (due.slice(0, 7) === today.slice(0, 7)) return { key: 'thismonth', label: 'This month', order: 2 };
      return { key: 'later', label: 'Later', order: 3 };
    }
    default: return NONE;
  }
}

// ZQL buckets, in order: the first whose answer holds the key wins.
// `setOf(zql)` is the page's answer store — undefined / 'loading' while the
// server is still answering, { keys } when it has, { error } when it failed.
export function zqlGroup(it, buckets, setOf, other = '') {
  const live = (buckets || []).filter((b) => b.zql && b.zql.trim());
  if (!live.length) return NONE;
  for (let i = 0; i < live.length; i += 1) {
    const b = live[i];
    const s = setOf(b.zql.trim());
    if (!s || s === 'loading') return PENDING;
    if (s.error) return { key: `~err${i}`, label: `(bucket “${b.label || b.zql}” failed: ${s.error})`, order: i };
    if (s.keys && s.keys.has(it && it.key)) return { key: `b${i}`, label: b.label || b.zql, order: i };
  }
  return { key: '~other', label: other || '(other)', order: live.length };
}

// ---- formulas ------------------------------------------------------------------
// A formula is a JavaScript expression (or a body with `return`) over one
// work item, written by an admin in the grouping's editor, evaluated in the
// browser with these helpers in scope. A thrown error is a group of its own,
// named after the error, never a blank page.
export const FORMULA_HELP = [
  ['key, summary, status, statusCategory, type, project, projectCategory, assignee, priority', 'the built-ins, as text'],
  ['value("Field name")', 'a field\'s value as text (a select, a user, a number…)'],
  ['number("Field name")', 'a field\'s value as a number, or null'],
  ['date("Field name")', 'a date field as yyyy-mm-dd, or "" — "duedate" and "created" work too'],
  ['day(x) week(x) month(x) quarter(x) year(x)', 'the bucket of a date (x = a field name or yyyy-mm-dd): sorts in date order'],
  ['daysUntil(x) daysSince(x)', 'whole days from today to a date, or since it'],
  ['today', 'today as yyyy-mm-dd'],
  ['bucket(key, label, order)', 'a group you name yourself, placed by `order`'],
  ['fields', 'the raw fields object, by field id'],
];
const NAMES = ['key', 'summary', 'status', 'statusCategory', 'type', 'project', 'projectCategory', 'assignee', 'priority',
  'value', 'number', 'date', 'day', 'week', 'month', 'quarter', 'year', 'daysUntil', 'daysSince', 'today', 'bucket', 'fields', 'item'];

export function compileFormula(src) {
  const text = String(src || '').trim();
  if (!text) return { fn: null, error: 'no formula' };
  const body = /\breturn\b/.test(text) ? text : `return (${text});`;
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('h', `"use strict"; const { ${NAMES.join(', ')} } = h; ${body}`);
    return { fn, error: null };
  } catch (e) { return { fn: null, error: (e && e.message) || 'bad formula' }; }
}

// The helpers for one work item. `resolve(name)` turns a field name into
// this site's field id (the page's resolver); `todayIso` is the page's today.
export function formulaHelpers(it, { resolve, todayIso } = {}) {
  const fl = f(it);
  const idOf = (name) => {
    const n = String(name || '').trim();
    if (!n) return '';
    if (/^customfield_\d+$/.test(n) || ['summary', 'duedate', 'created', 'updated', 'assignee', 'priority', 'status', 'issuetype', 'project', 'resolutiondate', 'resolution', 'labels'].includes(n)) return n;
    return (typeof resolve === 'function' && resolve(n)) || n;
  };
  const raw = (name) => { const id = idOf(name); return id ? fl[id] : undefined; };
  const value = (name) => textOf(raw(name));
  const number = (name) => { const v = raw(name); const n = typeof v === 'number' ? v : (v != null && v !== '' ? Number(textOf(v)) : NaN); return Number.isFinite(n) ? n : null; };
  const date = (x) => { const s = String(x == null ? '' : x); return isoOfValue(s) || isoOfValue(raw(s)); };
  const today = isoOfValue(todayIso) || fromDayNumber(Math.floor(Date.now() / 86400000));
  const at = (grain) => (x) => { const iso = date(x); return iso ? dateBucket(iso, grain, today) : NONE; };
  return {
    item: it, fields: fl,
    key: (it && it.key) || '', summary: textOf(fl.summary), status: textOf(fl.status), statusCategory: catOf(it),
    type: textOf(fl.issuetype), project: fl.project ? (fl.project.name || fl.project.key || '') : '',
    projectCategory: fl.project && fl.project.projectCategory ? textOf(fl.project.projectCategory) : '',
    assignee: textOf(fl.assignee), priority: textOf(fl.priority),
    value, number, date, today,
    day: at('day'), week: at('week'), month: at('month'), quarter: at('quarter'), year: at('year'),
    daysUntil: (x) => { const iso = date(x); return iso ? dayNumber(iso) - dayNumber(today) : null; },
    daysSince: (x) => { const iso = date(x); return iso ? dayNumber(today) - dayNumber(iso) : null; },
    bucket: (key, label, order) => ({ key: String(key), label: label == null ? String(key) : String(label), order: typeof order === 'number' ? order : null }),
  };
}

// What a formula answered, as a group.
export function toGroup(v) {
  if (v == null || v === '' || v === false) return NONE;
  if (typeof v === 'object' && !(v instanceof Date)) {
    if (v.key != null || v.label != null) {
      const g = { key: String(v.key != null ? v.key : v.label), label: String(v.label != null ? v.label : v.key), order: typeof v.order === 'number' ? v.order : null };
      return typeof v.tag === 'string' && v.tag ? { ...g, tag: v.tag } : g;
    }
    return NONE;
  }
  if (v instanceof Date) { const iso = Number.isNaN(v.getTime()) ? '' : v.toISOString().slice(0, 10); return iso ? dateBucket(iso, 'day') : NONE; }
  if (typeof v === 'number') return { key: String(v), label: String(v), order: v };
  const s = String(v);
  return { key: s, label: s, order: null };
}

export function formulaGroup(it, compiled, ctx) {
  if (!compiled || !compiled.fn) return { key: '~formula', label: `(formula: ${(compiled && compiled.error) || 'none'})`, order: LAST - 1 };
  try { return toGroup(compiled.fn(formulaHelpers(it, ctx))); }
  catch (e) { return { key: '~formula-error', label: `(formula error: ${(e && e.message) || e})`, order: LAST - 1 }; }
}
